import { Fragment } from "react"
import { NavLink, Outlet, useLocation } from "react-router-dom"
import { useLanguages } from "../api/queries"
import { useAuth } from "../auth/AuthContext"
import { useAccess } from "../auth/rights"
import { confirmDiscard } from "../hooks/useUnsaved"
import { useLanguageSwitch } from "../lib/language"

/** Меню. «Форма переводчика» отдельным пунктом не живёт: она влилась
 *  в справочник переключателями «Символы», «Ссылки на сайт» и выгрузкой.
 *
 *  `admin` — пункт виден только администратору. Такой один: «Каталоги и
 *  сайты» — состав каталогов, назначение товаров и заведение новых сайтов
 *  кладут в ERP боевые реквизиты (ФТП и БД сайта). Маршрут закрыт отдельно
 *  (`App.tsx`), меню его только не показывает. */
const NAV = [
	{
		section: "Контент",
		items: [
			{ to: "/groups", icon: "▤", label: "Группы товаров" },
			{ to: "/publish", icon: "↑", label: "Каталоги и сайты", admin: true },
		],
	},
	{
		section: "Переводы",
		items: [
			{ to: "/translate/dictionary", icon: "☰", label: "Справочник переводов" },
		],
	},
	{
		section: "Печать",
		items: [{ to: "/pdf", icon: "❐", label: "Генерация каталогов" }],
	},
	{
		section: "Справка",
		items: [{ to: "/guides", icon: "▶", label: "Видео-гайды" }],
	},
]

const TITLES: Record<string, string> = {
	"/groups": "Группы товаров",
	"/groups/new": "Новая группа",
	"/publish": "Каталоги и сайты",
	"/translate/dictionary": "Справочник переводов",
	"/pdf": "Генерация каталогов",
	"/guides": "Видео-гайды",
}

export default function Layout() {
	const { session, logout } = useAuth()
	const { isAdmin } = useAccess()
	const { pathname } = useLocation()
	const languages = useLanguages()
	const { languageId, setLanguageId } = useLanguageSwitch()

	const title =
		TITLES[pathname] ??
		(pathname.startsWith("/groups/")
			? "Группа товаров"
			: pathname.startsWith("/guides/")
				? "Видео-гайды"
				: "Avtodelo Rīga")

	const initials = (session?.displayName || session?.username || "?")
		.slice(0, 2)
		.toUpperCase()

	return (
		<div className="layout">
			<aside className="sidebar">
				<div className="sidebar__brand">
					<span className="sidebar__flag" />
					Avtodelo&nbsp;Rīga
				</div>
				<nav className="sidebar__nav">
					{NAV.map(group => (
						<Fragment key={group.section}>
							<div className="sidebar__section">{group.section}</div>
							{group.items
								.filter(item => !item.admin || isAdmin)
								.map(item => (
								<NavLink
									key={item.to}
									to={item.to}
									className={({ isActive }) =>
										"navlink" + (isActive ? " navlink--active" : "")
									}
								>
									<span className="navlink__icon">{item.icon}</span>
									{item.label}
								</NavLink>
							))}
						</Fragment>
					))}
				</nav>
				<div className="sidebar__foot">{session?.roles.join(", ") || "—"}</div>
			</aside>

			<div className="main">
				<header className="topbar">
					<div className="topbar__title">{title}</div>
					<div className="topbar__spacer" />

					<select
						className="select"
						value={languageId}
						onChange={event => {
							const next = Number(event.target.value)
							// смена языка пересобирает экран — предупреждаем о правках
							if (!confirmDiscard("Сменить язык и потерять их?")) {
								event.target.value = String(languageId)
								return
							}
							setLanguageId(next)
						}}
						title="Язык контента"
					>
						{(languages.data ?? []).map(language => (
							<option key={language.id} value={language.id}>
								{language.name}
							</option>
						))}
					</select>

					<div className="topbar__user">
						<span className="avatar">{initials}</span>
						{session?.username}
					</div>
					<button className="btn btn--ghost" onClick={logout}>
						Выйти
					</button>
				</header>

				<main className="content">
					{/* язык меняет всё содержимое экрана — пересоздаём его целиком */}
					<Outlet key={languageId} />
				</main>
			</div>
		</div>
	)
}
